import { EntityFieldDefinitionSemanticType } from '@moment/horizon-data-access/platform'

import { parseClientColumnId } from '@/shared/sorm/cross-entity-columns'
import {
  formatBoolean,
  formatBooleanAvailable,
  formatBooleanEnabledDisabled,
} from '@/utils/presentation/formatting'

/** Account fields that always render Available / Not available (overrides semantic_type). */
const BOOLEAN_AVAILABLE_FIELDS = new Set([
  'prime_brokerage_eligible',
  'margin_indicator',
])

export function parseSormBooleanValue(raw: unknown): boolean | null {
  if (raw == null) {
    return null
  }

  if (typeof raw === 'boolean') {
    return raw
  }

  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase()
    if (normalized === 'true') {
      return true
    }
    if (normalized === 'false') {
      return false
    }
  }

  return null
}

function isAvailabilityField({
  columnId,
  rootEntity,
}: {
  columnId: string
  rootEntity: string
}): boolean {
  const { field } = parseClientColumnId({ columnId, rootEntity })
  return BOOLEAN_AVAILABLE_FIELDS.has(field)
}

function formatParsedBoolean(
  parsed: boolean,
  semanticType: unknown,
  columnId: string,
  rootEntity: string,
): string {
  // Product copy for PB / margin — wins over schema semantic_type
  // (these fields are commonly tagged boolean_enabled_disabled).
  if (isAvailabilityField({ columnId, rootEntity })) {
    return formatBooleanAvailable(parsed)
  }

  if (
    semanticType === EntityFieldDefinitionSemanticType.boolean_enabled_disabled
  ) {
    return formatBooleanEnabledDisabled(parsed)
  }

  return formatBoolean(parsed)
}

/**
 * When the value is boolean (or true/false string) and the column is a bool field
 * or carries a boolean semantic type, returns the display string. Otherwise `undefined`.
 */
export function formatSormSemanticBooleanValue({
  value,
  fieldType,
  semanticType,
  columnId,
  rootEntity,
}: {
  value: unknown
  fieldType: string | undefined
  semanticType: unknown
  columnId: string
  rootEntity: string
}): string | undefined {
  const isBoolFieldType = fieldType === 'bool' || fieldType === 'boolean'
  const isBooleanYesNoSemantic =
    semanticType === EntityFieldDefinitionSemanticType.boolean_yes_no
  const isBooleanEnabledDisabledSemantic =
    semanticType === EntityFieldDefinitionSemanticType.boolean_enabled_disabled

  if (
    !isBoolFieldType &&
    !isBooleanYesNoSemantic &&
    !isBooleanEnabledDisabledSemantic
  ) {
    return undefined
  }

  const parsed = parseSormBooleanValue(value)
  if (parsed == null) {
    if (!isBoolFieldType) {
      return undefined
    }

    return String(value)
  }

  return formatParsedBoolean(parsed, semanticType, columnId, rootEntity)
}

/**
 * Maps filter option values (e.g. "true" / "false") to the same labels as grid cells.
 */
export function formatSormBooleanFilterOptionLabel({
  rawValue,
  semanticType,
  columnId,
  rootEntity,
}: {
  rawValue: string
  semanticType: unknown
  columnId: string
  rootEntity: string
}): string {
  const parsed = parseSormBooleanValue(rawValue)
  if (parsed == null) {
    return rawValue
  }

  return formatParsedBoolean(parsed, semanticType, columnId, rootEntity)
}
